import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Landmark, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

interface PropertyMortgagesProps {
  propertyId: string;
}

export function PropertyMortgages({ propertyId }: PropertyMortgagesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: mortgages = [], isLoading } = useQuery({
    queryKey: ['property-mortgages', propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_mortgages')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();
      if (!profile?.organization_id) throw new Error('Organization not found');

      const { error } = await supabase.from('property_mortgages').insert({
        property_id: propertyId,
        organization_id: profile.organization_id,
        bank_name: formData.get('bank_name') as string || null,
        initial_amount: parseFloat(formData.get('initial_amount') as string),
        remaining_amount: parseFloat(formData.get('remaining_amount') as string),
        monthly_payment: parseFloat(formData.get('monthly_payment') as string),
        interest_rate: formData.get('interest_rate') ? parseFloat(formData.get('interest_rate') as string) : null,
        start_date: formData.get('start_date') as string,
        end_date: formData.get('end_date') as string || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-mortgages', propertyId] });
      setIsOpen(false);
      toast.success('Échéancier ajouté');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('property_mortgages').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-mortgages', propertyId] });
      toast.success('Échéancier supprimé');
    },
  });

  const totalRemaining = mortgages.reduce((sum: number, m: any) => sum + Number(m.remaining_amount), 0);
  const totalMonthly = mortgages.reduce((sum: number, m: any) => sum + Number(m.monthly_payment), 0);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Landmark className="h-4 w-4" />
          Crédits immobiliers
        </CardTitle>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-1" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvel échéancier de crédit</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(new FormData(e.currentTarget));
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>Banque</Label>
                <Input name="bank_name" placeholder="Nom de la banque" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant initial *</Label>
                  <Input name="initial_amount" type="number" step="0.01" required />
                </div>
                <div className="space-y-2">
                  <Label>Capital restant *</Label>
                  <Input name="remaining_amount" type="number" step="0.01" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mensualité *</Label>
                  <Input name="monthly_payment" type="number" step="0.01" required />
                </div>
                <div className="space-y-2">
                  <Label>Taux (%)</Label>
                  <Input name="interest_rate" type="number" step="0.01" placeholder="2.5" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date début *</Label>
                  <Input name="start_date" type="date" required />
                </div>
                <div className="space-y-2">
                  <Label>Date fin</Label>
                  <Input name="end_date" type="date" />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Ajout...' : 'Ajouter'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="space-y-4">
        {mortgages.length > 0 && (
          <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
            <div>
              <p className="text-xs text-muted-foreground">Capital restant total</p>
              <p className="text-lg font-semibold text-destructive flex items-center gap-1">
                <TrendingDown className="h-4 w-4" />
                {formatCurrency(totalRemaining)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Mensualités totales</p>
              <p className="text-lg font-semibold">{formatCurrency(totalMonthly)}/mois</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Chargement...</p>
        ) : mortgages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun crédit enregistré</p>
        ) : (
          <div className="space-y-2">
            {mortgages.map((mortgage: any) => (
              <div key={mortgage.id} className="flex items-center justify-between p-3 rounded border">
                <div className="flex-1">
                  <p className="font-medium">{mortgage.bank_name || 'Banque non renseignée'}</p>
                  <p className="text-sm text-muted-foreground">
                    Restant: {formatCurrency(mortgage.remaining_amount)} / {formatCurrency(mortgage.initial_amount)}
                    {mortgage.interest_rate && ` • ${mortgage.interest_rate}%`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Mensualité: {formatCurrency(mortgage.monthly_payment)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteMutation.mutate(mortgage.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
